import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// 定義狀態類型
export interface BookingState {
  userType: 'local' | 'foreign' | null;
  transportOption: any | null;
  stayOption: any | null;
  workspaceOption: any | null;
  communityOptions: any[];
  cultureExperiences: any[];
  currentStep: number;
}

// 定義動作類型
type BookingAction =
  | { type: 'SET_USER_TYPE'; payload: 'local' | 'foreign' }
  | { type: 'SET_TRANSPORT'; payload: any }
  | { type: 'SET_STAY'; payload: any }
  | { type: 'SET_WORKSPACE'; payload: any }
  | { type: 'ADD_COMMUNITY'; payload: any }
  | { type: 'REMOVE_COMMUNITY'; payload: number }
  | { type: 'ADD_CULTURE_EXPERIENCE'; payload: any }
  | { type: 'REMOVE_CULTURE_EXPERIENCE'; payload: number }
  | { type: 'SET_STEP'; payload: number }
  | { type: 'RESET_BOOKING' };

// 初始狀態
const initialState: BookingState = {
  userType: null,
  transportOption: null,
  stayOption: null,
  workspaceOption: null,
  communityOptions: [],
  cultureExperiences: [],
  currentStep: 1,
};

// Reducer 函數
function bookingReducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case 'SET_USER_TYPE':
      return { ...state, userType: action.payload, currentStep: 2 };
    
    case 'SET_TRANSPORT':
      return { ...state, transportOption: action.payload, currentStep: 3 };
    
    case 'SET_STAY':
      return { ...state, stayOption: action.payload, currentStep: 4 };
    
    case 'SET_WORKSPACE':
      return { ...state, workspaceOption: action.payload, currentStep: 5 };
    
    case 'ADD_COMMUNITY':
      return { 
        ...state, 
        communityOptions: [...state.communityOptions, action.payload],
        currentStep: 6 
      };
    
    case 'REMOVE_COMMUNITY':
      return {
        ...state,
        communityOptions: state.communityOptions.filter((_, index) => index !== action.payload)
      };
    
    case 'ADD_CULTURE_EXPERIENCE':
      return { 
        ...state, 
        cultureExperiences: [...state.cultureExperiences, action.payload],
        currentStep: 7 
      };
    
    case 'REMOVE_CULTURE_EXPERIENCE':
      return {
        ...state,
        cultureExperiences: state.cultureExperiences.filter((_, index) => index !== action.payload)
      };
    
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    
    case 'RESET_BOOKING':
      return initialState;
    
    default:
      return state;
  }
}

// Context 類型
interface BookingContextType {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  setUserType: (type: 'local' | 'foreign') => void;
  setTransport: (option: any) => void;
  setStay: (option: any) => void;
  setWorkspace: (option: any) => void;
  addCommunity: (option: any) => void;
  removeCommunity: (index: number) => void;
  addCultureExperience: (option: any) => void;
  removeCultureExperience: (index: number) => void;
  setStep: (step: number) => void;
  resetBooking: () => void;
}

// 建立 Context
const BookingContext = createContext<BookingContextType | undefined>(undefined);

// Provider 元件
interface BookingProviderProps {
  children: ReactNode;
}

export const BookingProvider: React.FC<BookingProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  // 便利函數
  const setUserType = (type: 'local' | 'foreign') => {
    dispatch({ type: 'SET_USER_TYPE', payload: type });
  };

  const setTransport = (option: any) => {
    dispatch({ type: 'SET_TRANSPORT', payload: option });
  };

  const setStay = (option: any) => {
    dispatch({ type: 'SET_STAY', payload: option });
  };

  const setWorkspace = (option: any) => {
    dispatch({ type: 'SET_WORKSPACE', payload: option });
  };

  const addCommunity = (option: any) => {
    dispatch({ type: 'ADD_COMMUNITY', payload: option });
  };

  const removeCommunity = (index: number) => {
    dispatch({ type: 'REMOVE_COMMUNITY', payload: index });
  };

  const addCultureExperience = (option: any) => {
    dispatch({ type: 'ADD_CULTURE_EXPERIENCE', payload: option });
  };

  const removeCultureExperience = (index: number) => {
    dispatch({ type: 'REMOVE_CULTURE_EXPERIENCE', payload: index });
  };

  const setStep = (step: number) => {
    dispatch({ type: 'SET_STEP', payload: step });
  };

  const resetBooking = () => {
    dispatch({ type: 'RESET_BOOKING' });
  };

  const value: BookingContextType = {
    state,
    dispatch,
    setUserType,
    setTransport,
    setStay,
    setWorkspace,
    addCommunity,
    removeCommunity,
    addCultureExperience,
    removeCultureExperience,
    setStep,
    resetBooking,
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

// Hook
export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
}; 